import React, { useState, useEffect } from "react";
import { BasePopup } from "components/pop-up/BasePopup";
import ProsecutorCard from "components/card/ProsecutorCard";
import { useTooltip } from "context/TooltipProvider";
import {
  fetchProsecutors,
  updateAssetReferral,
} from "services/assetRecoveryService";
import { getAvatarBgColor } from "utils/helper";
import { LoginAccount } from "types";
import { useAuth } from "context/AuthContext";
import { useTranslation } from "react-i18next";

interface ProsecutorReassignPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referral: any;
  assignedProsecutor?: string;
  onProsecutorUpdated?: (response: any) => void;
}

interface Prosecutor {
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
  };
}

export const ProsecutorReassignPopup: React.FC<
  ProsecutorReassignPopupProps
> = ({
  isOpen,
  onClose,
  referral,
  assignedProsecutor,
  onProsecutorUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedProsecutor, setSelectedProsecutor] = useState<string>("");
  const [prosecutors, setProsecutors] = useState<Prosecutor[]>([]);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const { loginData } = useAuth();
  useEffect(() => {
    if (isOpen) {
      // Fetch prosecutors when popup opens
      fetchProsecutors(loginData as LoginAccount)
        .then(setProsecutors)
        .catch((error) => {
          console.error("Error fetching prosecutors:", error);
          showTooltip(t("failed_to_load_prosecutors"), "error");
        });

      // Set initial selected prosecutor
      if (assignedProsecutor) {
        setSelectedProsecutor(assignedProsecutor);
      } else if (referral?.assigned_prosecutor_user_id) {
        setSelectedProsecutor(referral.assigned_prosecutor_user_id);
      }
    }
  }, [isOpen, assignedProsecutor, referral]);

  const handleProsecutorChange = (value: string) => {
    setSelectedProsecutor(value);
  };

  const getInitials = (name: string): string => {
    if (!name) return "";
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleAssignReAssign = async () => {
    if (!selectedProsecutor) {
      showTooltip(t("please_select_prosecutor"), "error");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      // Update assigned prosecutor if changed
      if (selectedProsecutor !== referral?.assigned_prosecutor_user_id) {
        const selectedProsecutorData = prosecutors.find(
          (pros) => pros?.user?.id === selectedProsecutor,
        );

        if (selectedProsecutorData) {
          const fullName = `${selectedProsecutorData.user?.first_name} ${selectedProsecutorData.user?.last_name}`;
          formData.append("assigned_prosecutor", fullName);
          formData.append("assigned_prosecutor_user_id", selectedProsecutor);
        }
      }

      const response = await updateAssetReferral(referral?.id, formData);
      showTooltip(t("successfully_assigned"), "success");

      if (onProsecutorUpdated) {
        onProsecutorUpdated(response);
      }

      onClose();
    } catch (error) {
      console.error("Error assigning prosecutor:", error);
      showTooltip(t("failed_to_assign_prosecutor"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedProsecutor(
      assignedProsecutor || referral?.assigned_prosecutor_user_id || "",
    );
    onClose();
  };

  // Get initials and background color for the card
  let initials = "";
  let bgColor = "#6B7280";

  if (selectedProsecutor) {
    const selectedProsecutorData = prosecutors.find(
      (pros) => pros?.user?.id === selectedProsecutor,
    );
    if (selectedProsecutorData) {
      const fullName = `${selectedProsecutorData.user?.first_name} ${selectedProsecutorData.user?.last_name}`;
      initials = getInitials(fullName);
      bgColor = getAvatarBgColor(fullName);
    }
  } else if (referral?.assigned_prosecutor) {
    initials = getInitials(referral.assigned_prosecutor);
    bgColor = getAvatarBgColor(referral.assigned_prosecutor);
  }

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Prosecutor Reassignment"
      description="Assign or reassign prosecutor for this case"
    >
      <div className="relative pb-20">
        {" "}
        <div className="space-y-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Select Prosecutor *</label>
            <select
              className="referral-form-field"
              value={selectedProsecutor}
              onChange={(e) => handleProsecutorChange(e.target.value)}
              required
              disabled={loading}
            >
              <option value="">Select Prosecutor</option>
              {prosecutors?.map((pros) => (
                <option value={pros?.user?.id} key={pros?.user?.id}>
                  {pros.user?.first_name} {pros.user?.last_name}
                </option>
              ))}
            </select>
          </div>

          {(referral?.assigned_prosecutor || selectedProsecutor) && (
            <ProsecutorCard
              selectedProsecutor={selectedProsecutor}
              prosecutors={prosecutors}
              referral={referral}
            />
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex justify-end space-x-3 pt-4 bg-white border-t border-gray-200 p-4 -mx-4 -mb-4">
          <button
            onClick={handleAssignReAssign}
            disabled={loading || !selectedProsecutor}
            className={`referral-form-button text-white px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              loading || !selectedProsecutor
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {loading ? "Assigning..." : "Assign"}
          </button>
        </div>
      </div>
    </BasePopup>
  );
};

export default ProsecutorReassignPopup;
